import { LoadingPanelService } from "../../shared/loading-panel/loading-panel.service";
import { CurrentUserService } from '../../user/services';
import { GenericModalService, ModalSettingsTemplate } from "../../shared/generic-modal/generic-modal.service";
import { ToastService, ToastType } from '../../shared/toast/toast.service';
import { ConcurrencyKeysService } from '../services/concurrency-keys.service';
import { ConcurrencyKey, ConcurrencyTicketUI } from "../models/concurrency-key";
import { DateTimeService } from "../../shared/datetime/datetime.service";
import { GridService } from "../../shared/grid/grid.service";
import { Component, OnInit, TemplateRef, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { SvgIconComponent } from "../../shared/icons/svg-icon.component";
import { PermissionRequiredDirective } from "../../shared/authentication/permission-required.directive";
import { ChangeLogLinkComponent } from "../../shared/change-log/change-log-link/change-log-link.component";
import { ChangeLogLinkEntityDirective } from "../../shared/change-log/change-log-link-entity/change-log-link-entity.directive";
import { DxDataGridModule } from "devextreme-angular";
import { BizDataGridDirective } from "../../shared/grid/biz-grid.directive";
import { DxoEditingModule, DxoPopupModule, DxiColumnModule, DxiValidationRuleModule, DxiButtonModule } from "devextreme-angular/ui/nested";
import { BizUserColumnDirective } from "../../shared/grid/biz-grid-user.directive";
import { BizDateColumnDirective } from "../../shared/grid/biz-grid-date.directive";
import { DxTemplateModule } from "devextreme-angular/core";
import { BizGridFiltervalues } from 'src/app/shared/grid/biz-grid-filtervalues';

@Component({
    selector: 'app-concurrency-keys',
    templateUrl: './concurrency-keys.component.html',
    styleUrls: ['./concurrency-keys.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, DxDataGridModule, BizDataGridDirective, DxoEditingModule, DxoPopupModule, DxiColumnModule, DxiValidationRuleModule, BizUserColumnDirective, BizDateColumnDirective, DxiButtonModule, DxTemplateModule]
})
export class ConcurrencyKeysComponent implements OnInit {

  hasCreatePerm = false;
  hasEditPerm = false;
  hasDeletePerm = false;
  hasShowLogPerm = false;

  concurrencyKeys: ConcurrencyKey[] = [];
  @ViewChild('activeTicketsTemplate', { static: true }) activeTicketsTemplate: TemplateRef<any>;
  activeTickets: ConcurrencyTicketUI[];

  constructor(
      private currentUserService: CurrentUserService,
      private loadingPanelService: LoadingPanelService,
      private concurrencyService: ConcurrencyKeysService,
      private toastService: ToastService,
      private modalService: GenericModalService,
      public dateTimeService: DateTimeService,
      public bizGridFiltervalues: BizGridFiltervalues,
      private gridService: GridService) {
  }

  async ngOnInit() {
      this.hasCreatePerm = this.currentUserService.hasPermission('ConcurrencyKeys_Action_Create');
      this.hasEditPerm = this.currentUserService.hasPermission('ConcurrencyKeys_Action_Edit');
      this.hasDeletePerm = this.currentUserService.hasPermission('ConcurrencyKeys_Action_Delete');
      this.hasShowLogPerm = this.currentUserService.hasPermission('ConcurrencyKeys_Action_ShowLog');

      this.refresh();
  }

  async refresh() {
      try {
          this.loadingPanelService.showLoadingPanelDirect('Loading concurrency keys...');
          this.concurrencyKeys = await this.concurrencyService.getConcurrencyKeys();
      }
      finally {
          this.loadingPanelService.hideLoadingPanel();
      }
  }

  async onInitNewRow(e: any): Promise<void> {
      let newConfig = {} as ConcurrencyKey;
      newConfig.Name = '';
      newConfig.MaxConcurrentActivities = 10;
      e.data = newConfig;
  }

  onRowInserting = (e: any): void => {
      e.cancel = this.gridService.handleDataGridRow(e.data as ConcurrencyKey, item => this.saveConcurrencyKey(item));
  };

  onRowUpdating = (e: any): void => {
      e.cancel = this.gridService.handleDataGridRow({ ...e.key, ...e.newData } as ConcurrencyKey, item => this.saveConcurrencyKey(item), e.newData);
  };

  onRowRemoving = (e: any): void => {
      e.cancel = this.gridService.handleDataGridRow(e.data as ConcurrencyKey, item => this.removeConcurrencyKey(item));
  };

  async saveConcurrencyKey(concurrencyKey: ConcurrencyKey): Promise<ConcurrencyKey> {
      if (!this.isConcurrencyKeyNameUnique(concurrencyKey)) {
          throw new Error('Another concurrency key with the same name already exists.');
      }

      const newConcurrencyKey = await this.concurrencyService.saveConcurrencyKeys(concurrencyKey);
      this.toastService.showToast('Concurrency key saved...', ToastType.success);
      return newConcurrencyKey;
  }

  async removeConcurrencyKey(concurrencyKey: ConcurrencyKey): Promise<ConcurrencyKey> {
      concurrencyKey.IsDeleted = true;
      const deletedConcurrencyKey = await this.concurrencyService.saveConcurrencyKeys(concurrencyKey);
      this.toastService.showToast('Concurrency key deleted...', ToastType.success);
      return deletedConcurrencyKey;
  }

  isConcurrencyKeyNameUnique(concurrencyKey: ConcurrencyKey): boolean {
      if (this.concurrencyKeys.length > 0) {
          if (this.concurrencyKeys.some(ck => ck.Name === concurrencyKey.Name && ck.Id !== concurrencyKey.Id)) {
              return false;
          }
      }
      return true;
  }

  async showActiveTickets(concurrencyKeyId?: number) {
      await this.getActiveTickets(concurrencyKeyId);
      var settings = new ModalSettingsTemplate('lg', 'static');
      settings.windowClass = 'hugeModal';
      try {
          this.modalService.openTemplate(this.activeTicketsTemplate, settings);
      }
      catch { }
  }

  async getActiveTickets(concurrencyKeyId?: number) {
      this.loadingPanelService.showLoadingPanelDirect('Loading tickets...');
      this.activeTickets = await this.concurrencyService.getConcurrencyKeyTickets(concurrencyKeyId);
      this.loadingPanelService.hideLoadingPanel();
  }

  async onTicketRowRemoving(ticket: ConcurrencyTicketUI) {
      this.loadingPanelService.showLoadingPanelDirect('Releasing ticket...');
      await this.concurrencyService.releaseConcurrencyKeyTicket(ticket.Ticket.Value);
      this.loadingPanelService.hideLoadingPanel();
      await this.getActiveTickets();
  }
}
